import Microgame from '../engine/Microgame.js';

export default class FindGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Trouve !`, en: `Find!`, es: `¡Encuentra!` },
            controls: { fr: `Appuie sur l'intrus parmi les objets`, en: `Tap the odd one out among the objects`, es: `Toca el objeto diferente a los demás` },
            objective: { fr: `Identifie l'élément unique avant la fin`, en: `Identify the unique item before time is up`, es: `Identifica el objeto único antes del tiempo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 20, fromY: 30, toX: 80, toY: 30, duration: 300 },
                { type: 'swipe', fromX: 80, fromY: 50, toX: 20, toY: 50, duration: 300 },
                { type: 'swipe', fromX: 20, fromY: 70, toX: 80, toY: 70, duration: 300 },
                { type: 'wait', duration: 450 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.canvas = null;
        this.ctx = null;
        this.width = 0;
        this.height = 0;
        this.isCleaning = false;
        this.star = null;
        this.starX = 0;
        this.starY = 0;
        this.starSize = 80;
        this.lastPoint = null;
        this.found = false;

        // Progression ralentie : on amortit la vitesse pour ne pas que ce soit "impossible" à x4,
        // mais on garde un vrai défi : 0.3 est un bon compromis entre 0.1 (trop lent) et 0.5 (trop dur).
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeed = 1 + (speedMult - 1) * 0.35;

        // Base time : 4000ms. En x4 (adjusted 1.9), on aura environ ~2.1 secondes.
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "FIND!";

        this.width = this.container.clientWidth;
        this.height = this.container.clientHeight;

        // Background
        this.container.style.background = '#111';

        // The Hidden Star (Neon Blue)
        this.star = document.createElement('div');
        this.star.textContent = '★';
        this.star.style.fontSize = `${this.starSize}px`;
        this.star.style.color = this.primaryColor;
        this.star.style.textShadow = `0 0 20px ${this.primaryColor}, 0 0 40px ${this.primaryColor}`;
        this.star.style.position = 'absolute';
        this.star.style.zIndex = '1';
        this.star.style.display = 'flex';
        this.star.style.justifyContent = 'center';
        this.star.style.alignItems = 'center';
        this.star.style.lineHeight = '1';

        // Random Position (ensure it's fully inside)
        this.starX = Math.random() * (this.width - this.starSize);
        this.starY = Math.random() * (this.height - this.starSize);

        this.star.style.left = `${this.starX}px`;
        this.star.style.top = `${this.starY}px`;
        this.container.appendChild(this.star);

        // Create Canvas (The Dirt)
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.width;
        this.canvas.height = this.height;
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.zIndex = '10';
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Fill with "dirt" (Tinted)
        this.ctx.fillStyle = '#111'; // Dark base
        this.ctx.fillRect(0, 0, this.width, this.height);

        // Tint with secondary color
        this.ctx.globalAlpha = 0.2;
        this.ctx.fillStyle = this.secondaryColor;
        this.ctx.fillRect(0, 0, this.width, this.height);
        this.ctx.globalAlpha = 1.0;

        // Add texture
        for (let i = 0; i < 100; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            const size = Math.random() * 50 + 20;
            this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Darker spots
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    onDown(e) {
        if (!this.active || this.found) return;
        this.isCleaning = true;
        const rect = this.canvas.getBoundingClientRect();
        this.lastPoint = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        this.clean(this.lastPoint.x, this.lastPoint.y);
    }

    onMove(e) {
        if (!this.active || !this.isCleaning || this.found) return;
        const rect = this.canvas.getBoundingClientRect();
        const currentPoint = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };

        this.cleanLine(this.lastPoint, currentPoint);
        this.lastPoint = currentPoint;
    }

    onUp() {
        this.isCleaning = false;
    }

    cleanLine(p1, p2) {
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
        for (let i = 0; i < dist; i += 5) {
            const x = p1.x + Math.cos(angle) * i;
            const y = p1.y + Math.sin(angle) * i;
            this.clean(x, y);
        }
    }

    clean(x, y) {
        this.ctx.globalCompositeOperation = 'destination-out';
        this.ctx.beginPath();
        this.ctx.arc(x, y, 50, 0, Math.PI * 2);
        this.ctx.fill();

        this.checkStarFound();
    }

    checkStarFound() {
        if (this.found) return;

        // Check pixels ONLY in the star's area
        const padding = 20;
        const checkX = Math.floor(this.starX + padding);
        const checkY = Math.floor(this.starY + padding);
        const checkW = Math.floor(this.starSize - padding * 2);
        const checkH = Math.floor(this.starSize - padding * 2);

        // Safety check bounds
        if (checkX < 0 || checkY < 0 || checkX + checkW > this.width || checkY + checkH > this.height) return;

        const imageData = this.ctx.getImageData(checkX, checkY, checkW, checkH);
        const data = imageData.data;
        let transparentPixels = 0;

        // Check alpha channel
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] === 0) {
                transparentPixels++;
            }
        }

        const totalPixels = data.length / 4;
        const percentage = transparentPixels / totalPixels;

        // If 70% of the star's center is revealed, win
        if (percentage > 0.7) {
            this.found = true;
            this.isCleaning = false;

            // PAUSE THE TIMER!
            this.hideTimer = true;

            // Reveal fully
            this.canvas.style.opacity = '0';
            this.canvas.style.transition = 'opacity 0.5s';

            // Pulse the star
            this.star.animate([
                { transform: 'scale(1)' },
                { transform: 'scale(1.5)' },
                { transform: 'scale(1)' }
            ], {
                duration: 500,
                easing: 'ease-out'
            });

            // Wait before winning
            setTimeout(() => {
                this.win();
            }, 1000);
        }
    }

    // Ensure we don't lose if timeout happens during animation (just in case)
    onTimeout() {
        if (this.found) return true;
        return false;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
